import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const NewProjectPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    purchase_price: '',
    estimated_arv: '',
    target_budget: '',
    target_completion_date: '',
    notes: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address || !formData.city || !formData.state || !formData.zipcode) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.purchase_price || !formData.target_budget) {
      toast.error('Please enter purchase price and target budget');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_arv: parseFloat(formData.estimated_arv) || 0,
        target_budget: parseFloat(formData.target_budget) || 0,
      };
      
      const response = await projectAPI.create(payload);
      toast.success('Project created successfully!');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="new-project-page">
      <Button 
        variant="ghost" 
        onClick={() => navigate('/projects')}
        className="text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Projects
      </Button>

      <Card className="border-border bg-card rounded-sm">
        <CardHeader className="border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-bold font-heading">New Project</CardTitle>
              <CardDescription>Create a new fix & flip investment project</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Project Name *</Label>
              <Input
                id="name"
                name="name"
                placeholder="e.g., Oak Street Flip"
                value={formData.name}
                onChange={handleChange}
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                data-testid="project-name-input"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address">Street Address *</Label>
              <Input
                id="address"
                name="address"
                placeholder="123 Main Street"
                value={formData.address}
                onChange={handleChange}
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                data-testid="project-address-input"
              />
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  name="city"
                  placeholder="Austin"
                  value={formData.city}
                  onChange={handleChange}
                  className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                  data-testid="project-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State *</Label>
                <Select value={formData.state} onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}>
                  <SelectTrigger className="bg-input/50 border-border rounded-sm h-11" data-testid="project-state-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border rounded-sm max-h-[200px]">
                    {US_STATES.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipcode">Zip Code *</Label>
                <Input
                  id="zipcode"
                  name="zipcode"
                  placeholder="78701"
                  value={formData.zipcode}
                  onChange={handleChange}
                  className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                  data-testid="project-zipcode-input"
                />
              </div>
            </div>

            {/* Financial Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="purchase_price">Purchase Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="purchase_price"
                    name="purchase_price"
                    type="number"
                    placeholder="250,000"
                    value={formData.purchase_price}
                    onChange={handleChange}
                    className="bg-input/50 border-border focus:border-primary rounded-sm h-11 pl-7"
                    data-testid="project-purchase-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="target_budget">Renovation Budget *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="target_budget"
                    name="target_budget"
                    type="number"
                    placeholder="50,000"
                    value={formData.target_budget}
                    onChange={handleChange}
                    className="bg-input/50 border-border focus:border-primary rounded-sm h-11 pl-7"
                    data-testid="project-budget-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estimated_arv">Estimated ARV</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="estimated_arv"
                    name="estimated_arv"
                    type="number"
                    placeholder="350,000"
                    value={formData.estimated_arv}
                    onChange={handleChange}
                    className="bg-input/50 border-border focus:border-primary rounded-sm h-11 pl-7"
                    data-testid="project-arv-input"
                  />
                </div>
              </div>
            </div>

            {/* Target Completion Date */}
            <div className="space-y-2">
              <Label htmlFor="target_completion_date">Target Completion Date</Label>
              <Input
                id="target_completion_date"
                name="target_completion_date"
                type="date"
                value={formData.target_completion_date}
                onChange={handleChange}
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11"
                data-testid="project-completion-date-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                name="notes"
                placeholder="Any additional notes about this project..."
                value={formData.notes}
                onChange={handleChange}
                className="bg-input/50 border-border focus:border-primary rounded-sm min-h-[100px]"
                data-testid="project-notes-input"
              />
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/projects')}
                className="flex-1 rounded-sm h-11"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-11 btn-scale"
                disabled={loading}
                data-testid="create-project-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Project'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewProjectPage;
